import { memo } from "react";
import type { Row } from "../types.ts";
import { formatAmount, isoWeek } from "../format.ts";
import {
  focusAdjacentField,
  focusRowField,
  focusSiblingRow,
  nextSiblingRow,
} from "../focus.ts";
import { BellSnoozeIcon } from "@heroicons/react/20/solid";
import { CurrencyInput } from "./CurrencyInput.tsx";

type Props = {
  row: Row;
  balance: number;
  onPatch: (id: string, patch: Partial<Row>) => void;
  onDelete: (id: string) => void;
  onSnooze: (id: string) => void;
  onAddAfter: (dueDate: string, afterId: string) => void;
  onDateFocus: (id: string) => void;
  onDateBlur: () => void;
};

function LedgerRowImpl({
  row,
  balance,
  onPatch,
  onDelete,
  onSnooze,
  onAddAfter,
  onDateFocus,
  onDateBlur,
}: Props) {
  const amountClass =
    "col-amount num amount" +
    (row.amount < 0 ? " amount-neg" : row.amount > 0 ? " amount-pos" : "");

  return (
    <tr
      className={"row" + (row.amount > 0 ? " row-pos" : "")}
      data-row-id={row.id}
    >
      <td className="col-week num">{row.week}</td>
      <td className="col-due">
        <input
          type="date"
          required
          value={row.dueDate}
          onFocus={() => onDateFocus(row.id)}
          onChange={(e) => {
            const v = e.currentTarget.value;
            onPatch(row.id, { dueDate: v, week: isoWeek(v) });
          }}
          onBlur={() => onDateBlur()}
        />
      </td>
      <td className="col-supplier">
        <input
          type="text"
          value={row.supplier}
          onChange={(e) => onPatch(row.id, { supplier: e.currentTarget.value })}
          onKeyDown={(e) => {
            const inp = e.currentTarget;
            if (e.key === "Enter") {
              e.preventDefault();
              focusRowField(row.id, "amount");
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              focusSiblingRow(inp.closest("tr")!, "next", "supplier");
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              focusSiblingRow(inp.closest("tr")!, "prev", "supplier");
            } else if (
              e.key === "ArrowLeft" &&
              inp.selectionStart === 0 &&
              inp.selectionEnd === 0
            ) {
              e.preventDefault();
              focusAdjacentField(inp.closest("tr")!, "supplier", "prev");
            } else if (
              e.key === "ArrowRight" &&
              inp.selectionStart === inp.value.length &&
              inp.selectionEnd === inp.value.length
            ) {
              e.preventDefault();
              focusAdjacentField(inp.closest("tr")!, "supplier", "next");
            }
          }}
        />
      </td>
      <td className={amountClass}>
        <CurrencyInput
          value={row.amount}
          onChange={(n) => onPatch(row.id, { amount: n })}
          onKeyDown={(e) => {
            const inp = e.currentTarget;
            const tr = inp.closest("tr")!;
            if (e.key === "Enter") {
              e.preventDefault();
              onAddAfter(row.dueDate, row.id);
            } else if (e.key === "Tab" && !e.shiftKey) {
              const next = nextSiblingRow(tr);
              e.preventDefault();
              if (next?.dataset.rowId) {
                focusRowField(next.dataset.rowId, "supplier");
              } else {
                onAddAfter(row.dueDate, row.id);
              }
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              focusSiblingRow(tr, "next", "amount");
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              focusSiblingRow(tr, "prev", "amount");
            } else if (
              e.key === "ArrowLeft" &&
              inp.selectionStart === 0 &&
              inp.selectionEnd === 0
            ) {
              e.preventDefault();
              focusAdjacentField(tr, "amount", "prev");
            } else if (
              e.key === "ArrowRight" &&
              inp.selectionStart === inp.value.length &&
              inp.selectionEnd === inp.value.length
            ) {
              e.preventDefault();
              focusAdjacentField(tr, "amount", "next");
            }
          }}
        />
      </td>
      <td className={`col-balance num${balance < 0 ? " balance-neg" : ""}`}>
        {formatAmount(balance)}
      </td>
      <td className="col-delete">
        <button
          className="snooze-btn"
          tabIndex={-1}
          title="Snooza – flytta ut ur kalkylen"
          aria-label="Snooza"
          onClick={() => onSnooze(row.id)}
        >
          <BellSnoozeIcon aria-hidden="true" />
        </button>
        <button
          className="delete-btn"
          tabIndex={-1}
          title="Ta bort rad"
          onClick={() => onDelete(row.id)}
        >
          ×
        </button>
      </td>
    </tr>
  );
}

export const LedgerRow = memo(LedgerRowImpl);
